import { useEffect, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import CheckIcon0 from "../../assets/img/checkIcon02.svg";
import {
  convertCurrencyFormat,
  getUpdatedExchangeIncome,
} from "../../helper/common";
import {
  TAX_VALUE_FIELD,
  getTaxDocFieldKeys,
  getTaxDocFieldValue,
  getTaxDocMonthlyAmount,
  getTaxDocMonths,
  getTaxDocTitle,
  getTaxGroupLabel,
  isForeignTaxDoc,
} from "../../helper/taxSelection";
import { Button, Icon } from "../design";
import IncomePanel, {
  IncomePanelDoc,
  IncomePanelLatest,
  IncomePanelMode,
  IncomePanelOption,
  IncomePanelSource,
} from "./IncomePanel";

// Company login tenants also get Form 200 / 390 (corporate tax return,
// annual VAT summary); normal tenants never have those documents.
const MODEL_ORDER = ["130", "303", "100", "200", "390"];

/**
 * Tax side panel (same pattern as the payslip panel).
 * Step 2: documents grouped by model (tick to include, Latest tag on the
 *         default document, Details -> step 3, View -> new tab).
 * Step 3: figures of one document (Model 100 checkboxes; Model 130 / 303
 *         a single value).
 * Back on step 3 returns to step 2; on step 2 it closes the panel.
 */
const TaxSelectionModal = ({
  isOpen,
  onClose,
  documents = [],
  selectionMap = {},
  latestId = null,
  isDeaTaxRule = false,
  onToggleDoc,
  onToggleField,
  monthlyTotal = 0,
  originalUser,
  getDocumentUrl,
  onViewDetails,
  // Sum / Mean of the selected documents
  mode = "sum",
  onModeChange,
}) => {
  const { _t } = useTranslator();

  const [level, setLevel] = useState(2);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLevel(2);
      setActiveId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const activeDoc =
    level === 3 ? (documents.find((d) => d.id === activeId) ?? null) : null;
  const toEur = (doc, value) =>
    getUpdatedExchangeIncome(value ?? 0, doc?.currency, originalUser) || 0;
  const eur = (doc, value) => convertCurrencyFormat(toEur(doc, value));
  const converted = (doc) =>
    doc?.currency && doc.currency !== "EUR" ? ` (${_t("converted")})` : "";
  // figure keys come from the backend and are translated as they are
  const fieldLabel = (f) => _t(f);
  const docTitle = (doc) => getTaxDocTitle(doc, _t);

  const backToList = () => {
    setLevel(2);
    setActiveId(null);
  };

  // "Net accumulated income (box 03): €1,195/mo" style meta line
  const docMeta = (doc) => {
    const fields = selectionMap[doc.id] ?? [];
    let label;
    let amount;
    if (doc.model === "100" || doc.model === "200") {
      label =
        fields.length === 1
          ? fieldLabel(fields[0])
          : fields.length > 1
            ? `${fields.length} ${_t("figures")}`
            : _t("notSelected");
      amount = getTaxDocMonthlyAmount(doc, fields, originalUser);
    } else {
      label =
        doc.model === "130"
          ? fieldLabel("netAccumulatedIncome03")
          : doc.model === "390"
            ? fieldLabel("volumenOperaciones99")
            : _t("totalInvoicing");
      amount = getTaxDocMonthlyAmount(doc, [TAX_VALUE_FIELD], originalUser);
    }
    return `${label}: €${convertCurrencyFormat(amount)}/${_t("mo")}${converted(
      doc
    )}`;
  };

  // grouped by model; foreign returns get their own group, named without
  // the Spanish model number ("Annual tax report")
  const groups = MODEL_ORDER.flatMap((model) =>
    [false, true].map((foreign) => ({
      key: `${model}${foreign ? "-foreign" : ""}`,
      label: getTaxGroupLabel(model, foreign, _t),
      docs: documents
        .filter((d) => d.model === model && isForeignTaxDoc(d) === foreign)
        .sort((a, b) => b.year - a.year || (b.quarter ?? 0) - (a.quarter ?? 0)),
    }))
  ).filter((g) => g.docs.length);

  const renderList = () => (
    <>
      {groups.map(({ key, label, docs }) => (
        <div key={key} className="incomePanel__group">
          <h3 className="incomePanel__heading">{label}</h3>
          <div className="incomePanel__list">
            {docs.map((doc) => (
              <IncomePanelDoc
                key={doc.id}
                isOn={(selectionMap[doc.id] ?? []).length > 0}
                title={docTitle(doc)}
                badges={
                  <>
                    {doc.id === latestId && <IncomePanelLatest />}
                    {doc?.verified && (
                      <img
                        src={CheckIcon0}
                        alt="Verified"
                        className="incomePanel__verified"
                      />
                    )}
                  </>
                }
                meta={docMeta(doc)}
                onToggle={() => onToggleDoc(doc)}
                onDetails={() => {
                  setActiveId(doc.id);
                  setLevel(3);
                }}
                viewUrl={getDocumentUrl?.(doc)}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );

  // one selectable figure: annual value ÷ months -> monthly
  const FieldOption = ({ field, label, value, months, note }) => (
    <IncomePanelOption
      isOn={(selectionMap[activeDoc.id] ?? []).includes(field)}
      label={label}
      sub={`€${eur(activeDoc, value)} ÷ ${months}${note ? ` · ${note}` : ""}`}
      value={`€${convertCurrencyFormat(
        toEur(activeDoc, (value ?? 0) / months)
      )}/${_t("mo")}${converted(activeDoc)}`}
      onToggle={() => onToggleField(activeDoc, field)}
    />
  );

  // figure shown for information only (Model 303 box groups)
  const InfoOption = ({ label, value }) =>
    !value ? null : (
      <IncomePanelOption
        info
        label={label}
        value={`€${eur(activeDoc, value)}${converted(activeDoc)}`}
      />
    );

  const renderFields = () => {
    const months = getTaxDocMonths(activeDoc);
    return (
      <>
        <IncomePanelSource
          title={docTitle(activeDoc)}
          viewUrl={getDocumentUrl?.(activeDoc)}
        />
        <div className="incomePanel__list">
          {(activeDoc.model === "100" || activeDoc.model === "200") &&
            getTaxDocFieldKeys(activeDoc, isDeaTaxRule).map((f) => (
              <FieldOption
                key={f}
                field={f}
                label={fieldLabel(f)}
                value={getTaxDocFieldValue(activeDoc, f)}
                months={months}
              />
            ))}
          {activeDoc.model === "130" && (
            <FieldOption
              field={TAX_VALUE_FIELD}
              label={fieldLabel("netAccumulatedIncome03")}
              value={getTaxDocFieldValue(activeDoc)}
              months={months}
              note={
                activeDoc.quarter
                  ? `${_t("yearToDate")} Q${activeDoc.quarter}`
                  : _t("quarterUnknown")
              }
            />
          )}
          {activeDoc.model === "390" && (
            <FieldOption
              field={TAX_VALUE_FIELD}
              label={fieldLabel("volumenOperaciones99")}
              value={getTaxDocFieldValue(activeDoc)}
              months={months}
            />
          )}
          {activeDoc.model === "303" && (
            <>
              <InfoOption
                label={fieldLabel("sales")}
                value={getTaxDocFieldValue(activeDoc, "sales")}
              />
              <InfoOption
                label={fieldLabel("intraEu")}
                value={getTaxDocFieldValue(activeDoc, "intraEu")}
              />
              <FieldOption
                field={TAX_VALUE_FIELD}
                label={_t("totalInvoicing")}
                value={getTaxDocFieldValue(activeDoc)}
                months={months}
              />
            </>
          )}
        </div>
        {onViewDetails && (
          <div className="incomePanel__more">
            <Button
              variant="secondary"
              size="sm"
              iconEnd={<Icon name="chevronRight" size={14} />}
              onClick={onViewDetails}
            >
              {_t("viewMoreDetails")}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <IncomePanel
      isOpen={isOpen}
      onClose={onClose}
      onBack={activeDoc ? backToList : onClose}
      title={activeDoc ? docTitle(activeDoc) : _t("taxReturns")}
      crumbs={[
        { label: _t("income"), onClick: onClose },
        { label: _t("taxReturns"), onClick: backToList },
        ...(activeDoc ? [{ label: docTitle(activeDoc) }] : []),
      ]}
      total={monthlyTotal}
      actionLabel={activeDoc ? _t("back") : _t("done")}
      onAction={activeDoc ? backToList : onClose}
      footerExtra={
        activeDoc ? null : (
          <IncomePanelMode
            mode={mode}
            onChange={onModeChange}
            count={
              documents.filter((d) => (selectionMap[d.id] ?? []).length > 0)
                .length
            }
          />
        )
      }
    >
      {activeDoc ? renderFields() : renderList()}
    </IncomePanel>
  );
};

export default TaxSelectionModal;
